import React from "react";
import {
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    FormHelperText,
    Switch,
    makeStyles,
    Theme,
    createStyles,
    Button,
    CircularProgress,
    Typography
} from "@material-ui/core";
import { apiPut, useDataUnlessForbidden } from "../../services/api";
import { useSnackbar } from "notistack";
import IUser from "../../models/IUser";
import Loading from "../shared/Loading";
import { TRACER_VISION } from "../../assets/Constants";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        viewProjectButton: {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,

            padding: theme.spacing(1, 10, 1, 10),
            fontSize: "1rem",
            "&:hover": {
                backgroundColor: theme.palette.primary.dark
            },
            "&:disabled": {
                backgroundColor: "#bdbdbd",
                color: "#f5f5f5"
            }
        }
    })
);

export interface IPreferencesFormProps {
    userId?: string;
    onPreferencesUpdated?(): void;
}
export const UserPreferencesForm: React.FC<IPreferencesFormProps> = props => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [user, loadUser] = useDataUnlessForbidden<IUser>(
        props.userId
            ? `users/all/${props.userId}/preferences`
            : `users/me/preferences`,
        () =>
            enqueueSnackbar(
                "We couldn't retrieve user preferences. Please refresh and try again.",
                { variant: "error" }
            )
    );

    const [
        receiveInAppNotifications,
        setReceiveInAppNotifications
    ] = React.useState(false);

    const onReceiveInAppNotificationsChange = (event: {
        target: { checked: React.SetStateAction<boolean> };
    }) => {
        setReceiveInAppNotifications(event.target.checked);
    };

    const [
        receiveEmailNotifications,
        setReceiveEmailNotifications
    ] = React.useState(false);
    const onReceiveEmailNotificationsChange = (event: {
        target: { checked: React.SetStateAction<boolean> };
    }) => {
        setReceiveEmailNotifications(event.target.checked);
    };

    const [
        notifyWhenSamplesReceived,
        setNotifyWhenSamplesReceived
    ] = React.useState(false);

    const onNotifyWhenSamplesReceivedChange = (event: {
        target: { checked: React.SetStateAction<boolean> };
    }) => {
        setNotifyWhenSamplesReceived(event.target.checked);
    };

    const [
        notifyWhenDocumentsUploaded,
        setNotifyWhenDocumentsUploaded
    ] = React.useState(false);
    const onNotifyWhenDocumentsUploadedChange = (event: {
        target: { checked: React.SetStateAction<boolean> };
    }) => {
        setNotifyWhenDocumentsUploaded(event.target.checked);
    };

    const [
        notifyWhenTracerVisionUpdate,
        setNotifyWhenTracerVisionUpdate
    ] = React.useState(false);
    const onNotifyWhenTracerVisionUpdateChange = (event: {
        target: { checked: React.SetStateAction<boolean> };
    }) => {
        setNotifyWhenTracerVisionUpdate(event.target.checked);
    };

    const [
        notifyWhenTracerVisionReceivesNewComment,
        setNotifyWhenTracerVisionReceivesNewComment
    ] = React.useState(false);
    const onNotifyWhenTracerVisionReceivesNewCommentChange = (event: {
        target: { checked: React.SetStateAction<boolean> };
    }) => {
        setNotifyWhenTracerVisionReceivesNewComment(event.target.checked);
    };

    React.useEffect(() => {
        if (user) {
            setNotifyWhenDocumentsUploaded(user.notifyWhenDocumentsUploaded);
            setNotifyWhenSamplesReceived(user.notifyWhenSamplesReceived);
            setNotifyWhenTracerVisionReceivesNewComment(
                user.notifyWhenTracerVisionReceivesNewComment
            );
            setNotifyWhenTracerVisionUpdate(user.notifyWhenTracerVisionUpdate);
            setReceiveEmailNotifications(user.receiveEmailNotifications);
            setReceiveInAppNotifications(user.receiveInAppNotifications);
        }
    }, [user, loadUser]);

    const updatePreferences = async () => {
        const preferences = {
            receiveInAppNotifications: receiveInAppNotifications,
            receiveEmailNotifications: receiveEmailNotifications,
            notifyWhenSamplesReceived: notifyWhenSamplesReceived,
            notifyWhenDocumentsUploaded: notifyWhenDocumentsUploaded,
            notifyWhenTracerVisionUpdate: notifyWhenTracerVisionUpdate,
            notifyWhenTracerVisionReceivesNewComment: notifyWhenTracerVisionReceivesNewComment
        };
        await apiPut(
            props.userId
                ? `users/all/${props.userId}/preferences`
                : `users/me/preferences`,
            preferences
        );

        enqueueSnackbar("Preferences updated.", { variant: "success" });

        if (props.onPreferencesUpdated) {
            props.onPreferencesUpdated();
        }
    };

    return (
        <>
            {loadUser && (
                <>
                    <FormControl component="fieldset">
                        <Loading>
                            <CircularProgress />
                        </Loading>
                        <Typography
                            variant="subtitle1"
                            style={{
                                position: "absolute",
                                left: "-15px",
                                top: "60px"
                            }}
                        >
                            Loading Preferences...
                        </Typography>
                    </FormControl>
                </>
            )}

            {user && (
                <>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">
                            Notification Methods
                        </FormLabel>
                        <FormHelperText>
                            Controls how Tracerco we get in contact with you
                            about changes to your project(s)
                        </FormHelperText>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Switch
                                        color="primary"
                                        name="receiveInAppNotifications"
                                        checked={receiveInAppNotifications}
                                        onChange={
                                            onReceiveInAppNotificationsChange
                                        }
                                    />
                                }
                                label="Browser Notifications"
                            />
                            <FormHelperText>
                                You will only recieve in browser notifications
                                whilst on the site.
                            </FormHelperText>

                            <FormControlLabel
                                control={
                                    <Switch
                                        color="primary"
                                        name="receiveEmailNotifications"
                                        checked={receiveEmailNotifications}
                                        onChange={
                                            onReceiveEmailNotificationsChange
                                        }
                                    />
                                }
                                label="E-Mail"
                            />
                            <FormHelperText>
                                Notifications will be delivered directly to your
                                inbox.
                            </FormHelperText>
                        </FormGroup>

                        <br />

                        <br />

                        <FormLabel component="legend">
                            Notification Types
                        </FormLabel>
                        <FormHelperText>
                            Controls what changes Tracerco will notify you about
                            your project(s)
                        </FormHelperText>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Switch
                                        color="primary"
                                        name="notifyWhenSamplesReceived"
                                        checked={notifyWhenSamplesReceived}
                                        onChange={
                                            onNotifyWhenSamplesReceivedChange
                                        }
                                    />
                                }
                                label="New samples received"
                            />
                            <FormHelperText>
                                When we receive new samples from you
                            </FormHelperText>

                            <FormControlLabel
                                control={
                                    <Switch
                                        color="primary"
                                        name="notifyWhenTracerVisionUpdate"
                                        checked={notifyWhenTracerVisionUpdate}
                                        onChange={
                                            onNotifyWhenTracerVisionUpdateChange
                                        }
                                    />
                                }
                                label={`${TRACER_VISION} Updates`}
                            />
                            <FormHelperText>
                                Changes to your TracerVision&trade;
                            </FormHelperText>

                            <FormControlLabel
                                control={
                                    <Switch
                                        color="primary"
                                        name="notifyWhenTracerVisionReceivesNewComment"
                                        checked={
                                            notifyWhenTracerVisionReceivesNewComment
                                        }
                                        onChange={
                                            onNotifyWhenTracerVisionReceivesNewCommentChange
                                        }
                                    />
                                }
                                label={`${TRACER_VISION} Comments`}
                            />
                            <FormHelperText>
                                When a new comment is posted to any of your
                                TracerVision&trade; reports.
                            </FormHelperText>

                            <FormControlLabel
                                control={
                                    <Switch
                                        color="primary"
                                        name="notifyWhenDocumentsUploaded"
                                        checked={notifyWhenDocumentsUploaded}
                                        onChange={
                                            onNotifyWhenDocumentsUploadedChange
                                        }
                                    />
                                }
                                label="Documents Uploaded"
                            />
                            <FormHelperText>
                                When new documents are added to your project(s).
                            </FormHelperText>
                        </FormGroup>

                        <br />
                        <br />
                        <Button
                            onClick={updatePreferences}
                            className={classes.viewProjectButton}
                        >
                            Save Preferences
                        </Button>
                    </FormControl>
                </>
            )}
        </>
    );
};

export default UserPreferencesForm;
